/**
 * BabyCarePage
 *
 * Main page for baby care coordination feature.
 * Allows tracking of feeding, diaper changes, sleep, medication, temperature, and notes.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Baby, BarChart3, Pill } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { isValidUUID } from '@/lib/utils';
import { getBabyName } from '@/lib/babyUtils';
import {
  useBabyCare,
  BabyCareQuickActions,
  BabyCareTimeline,
  BabyCareEntryModal,
  ActiveSleepTimerCard,
  useMedicationReminders,
  type BabyCareEntry,
} from '../index';
import { useSleepPlan } from '@/features/sleep-plan/hooks/useSleepPlan';
import { BabyCareTodayDetail } from '../components/BabyCareTodayDetail';
import { MedicationReminderCard } from '../components/MedicationReminderCard';
import { updateMedicationReminder } from '@/services/medicationReminderService';

interface DueReminder {
  reminder: import('@/services/medicationReminderService').MedicationReminder;
  logId: string | null;
  scheduledFor: string | null;
}

export function BabyCarePage() {
  const { t } = useTranslation('babyCare');
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const isBabyCareEnabled = useFeatureEnabled('baby_care_enabled');

  // Validate pregnancy ID
  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Check if user is in baby mode with birth completion
  const wasBirth = lastCompletedPregnancy?.completion_type === 'birth';
  const isBabyMode = appMode === 'baby' && wasBirth;

  // Get baby care data
  const {
    entries,
    isLoading,
    isLoadingMore,
    hasMoreEntries,
    addEntry,
    deleteEntry,
    loadMoreEntries,
  } = useBabyCare();

  // Check if sleep plan is in learn/active mode
  const { settings: sleepPlanSettings } = useSleepPlan();
  const sleepPlanActiveMode = sleepPlanSettings?.isActive && (sleepPlanSettings.mode === 'learn' || sleepPlanSettings.mode === 'active');

  // Edit state for sleep entries
  const [editingSleepEntry, setEditingSleepEntry] = useState<BabyCareEntry | null>(null);

  // Get medication reminders and pending logs
  const { activeReminders, pendingLogs, acknowledgeLog } = useMedicationReminders();
  const [dismissedReminders, setDismissedReminders] = useState<Set<string>>(new Set());

  // Periodic tick to re-evaluate time-dependent state (due reminders)
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  // Build list of due reminders from two sources:
  // 1. Pending logs (cron sent notification, user hasn't acknowledged yet) — persistent
  // 2. next_reminder_at <= now (cron hasn't processed yet) — brief window
  const dueReminders = useMemo(() => {
    const now = new Date();
    const result: DueReminder[] = [];
    const seen = new Set<string>();

    // Source 1: Pending logs — these persist until the user confirms or dismisses
    for (const log of pendingLogs) {
      if (dismissedReminders.has(log.reminder_id)) continue;
      const reminder = activeReminders.find((r) => r.id === log.reminder_id);
      if (!reminder) continue;
      if (seen.has(reminder.id)) continue;
      seen.add(reminder.id);
      result.push({ reminder, logId: log.id, scheduledFor: log.scheduled_for });
    }

    // Source 2: next_reminder_at <= now (cron hasn't picked up yet)
    for (const r of activeReminders) {
      if (seen.has(r.id)) continue;
      if (dismissedReminders.has(r.id)) continue;
      if (!r.next_reminder_at) continue;
      if (new Date(r.next_reminder_at) <= now) {
        seen.add(r.id);
        result.push({ reminder: r, logId: null, scheduledFor: r.next_reminder_at });
      }
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReminders, pendingLogs, dismissedReminders, nowTick]);

  const handleMedConfirm = useCallback(async (reminderId: string, takenAt: string) => {
    const reminder = activeReminders.find(r => r.id === reminderId);
    if (!reminder || !pregnancyId) return;

    // Acknowledge the log entry if one exists
    const due = dueReminders.find((d) => d.reminder.id === reminderId);
    if (due?.logId) {
      await acknowledgeLog(due.logId, true, takenAt);
    }

    // Log as baby_care_entries medication entry
    await addEntry({
      pregnancy_id: pregnancyId,
      care_type: 'medication',
      baby_index: reminder.baby_index,
      recorded_at: takenAt,
      medication_name: reminder.medication_name,
      medication_dose: reminder.medication_dose ?? undefined,
      notes: reminder.notes ?? undefined,
    });
  }, [activeReminders, pregnancyId, addEntry, dueReminders, acknowledgeLog]);

  const handleMedDismiss = useCallback((reminderId: string) => {
    // Acknowledge the log entry as dismissed
    const due = dueReminders.find((d) => d.reminder.id === reminderId);
    if (due?.logId) {
      acknowledgeLog(due.logId, false);
    }
    setDismissedReminders(prev => new Set(prev).add(reminderId));
  }, [dueReminders, acknowledgeLog]);

  const handleMedAdjustSchedule = useCallback(async (reminderId: string, newBaseTime: string) => {
    const reminder = activeReminders.find(r => r.id === reminderId);
    if (!reminder) return;

    if (reminder.schedule_mode === 'interval') {
      // For interval-based: the next_reminder_at will auto-recalculate from the new base
      // No change needed — the cron will pick up from current time
    } else if (reminder.schedule_mode === 'fixed_times' && reminder.fixed_times.length > 0) {
      // For fixed times: shift all times by the offset between scheduled and actual
      const due = dueReminders.find(d => d.reminder.id === reminderId);
      const scheduledTime = due?.scheduledFor ? new Date(due.scheduledFor) : null;
      const actualTime = new Date(newBaseTime);

      if (scheduledTime) {
        const offsetMs = actualTime.getTime() - scheduledTime.getTime();
        const offsetMinutes = Math.round(offsetMs / 60000);

        const newFixedTimes = reminder.fixed_times.map((time: string) => {
          const [h, m] = time.split(':').map(Number);
          const totalMinutes = h * 60 + m + offsetMinutes;
          const newH = Math.floor(((totalMinutes % 1440) + 1440) % 1440 / 60);
          const newM = ((totalMinutes % 60) + 60) % 60;
          return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
        });

        await updateMedicationReminder(reminderId, { fixed_times: newFixedTimes });
      }
    }
  }, [activeReminders, dueReminders]);

  // Show disabled state if feature is not enabled
  if (!isBabyCareEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Show message if not in baby mode or no birth completion
  if (!isBabyMode || !pregnancyId) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Baby className="w-8 h-8 text-white/40" />
        </div>
        <h2 className="text-white font-medium text-lg mb-2">{t('pageTitle')}</h2>
        <p className="text-white/60 text-sm max-w-xs mx-auto">
          {t('notAvailable')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header with Stats & Medication Plan Buttons */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-white text-xl font-semibold">{t('pageTitle')}</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/tools/baby-care/medications"
            className="h-10 px-3 rounded-[10px] glass-effect flex items-center gap-1.5 text-white/70 hover:text-white hover:bg-white/15 transition-colors"
          >
            <Pill className="w-5 h-5" />
            <span className="text-sm font-medium">Med-Plan</span>
          </Link>
          <Link
            to="/tools/baby-care/stats"
            className="w-10 h-10 rounded-[10px] glass-effect flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <section className="mb-6">
        <BabyCareQuickActions pregnancyId={pregnancyId} sleepPlanActiveMode={!!sleepPlanActiveMode} />
      </section>

      {/* Active Sleep Timer */}
      <section className="mb-6">
        <ActiveSleepTimerCard />
      </section>

      {/* Active Medication Reminders */}
      {dueReminders.length > 0 && (
        <section className="mb-6 space-y-3">
          {dueReminders.map(({ reminder, scheduledFor }) => (
            <MedicationReminderCard
              key={reminder.id}
              reminder={reminder}
              babyName={getBabyName(lastCompletedPregnancy, reminder.baby_index)}
              scheduledFor={scheduledFor ?? undefined}
              onConfirm={handleMedConfirm}
              onDismiss={handleMedDismiss}
              onAdjustSchedule={handleMedAdjustSchedule}
            />
          ))}
        </section>
      )}

      {/* Today's Detail */}
      <section className="mb-6">
        <BabyCareTodayDetail entries={entries} />
      </section>

      {/* Timeline / History */}
      <section>
        <h2 className="text-white/70 text-sm font-medium mb-3">{t('history')}</h2>
        <BabyCareTimeline
          entries={entries}
          onDelete={deleteEntry}
          onEditSleep={setEditingSleepEntry}
          hasMore={hasMoreEntries}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMoreEntries}
        />
      </section>

      {/* Sleep Entry Edit Modal */}
      {editingSleepEntry && (
        <BabyCareEntryModal
          careType="sleep"
          pregnancyId={pregnancyId}
          editEntry={editingSleepEntry}
          onClose={() => setEditingSleepEntry(null)}
          sleepPlanMode={sleepPlanSettings?.mode}
        />
      )}
    </>
  );
}
